import type { CatalogItem } from '@/entities/catalog-item';

/**
 * Orders period rows by their numeric `Period` index ("1", "2", "10", …). Guards
 * against a non-numeric `Period`: `Number('M1')` is `NaN`, and a comparator that
 * returns `NaN` leaves the sort order implementation-defined. Unparseable values
 * sort last, then by string, so the result is always deterministic.
 */
function comparePeriods(a: CatalogItem, b: CatalogItem): number {
  const aValue = Number(a.Period);
  const bValue = Number(b.Period);
  const aIsNaN = Number.isNaN(aValue);
  const bIsNaN = Number.isNaN(bValue);
  if (aIsNaN || bIsNaN) {
    if (aIsNaN && bIsNaN) return a.Period.localeCompare(b.Period);
    return aIsNaN ? 1 : -1;
  }
  return aValue - bValue;
}

/**
 * Regroups the raw (pre-collapse) Spot catalog by `Code` so each code maps to
 * all of its period rows. These are the sibling rows the one-per-code collapse
 * in `useFilteredCatalogData` discards; the "Forward periods inline" sub-table
 * renders them. Empty for Forward Curves (no per-period spot values there).
 */
export function buildPeriodGroups(
  catalog: CatalogItem[] | undefined,
  showDiscontinued: boolean,
  isForwardCurves: boolean
): Map<string, CatalogItem[]> {
  const groups = new Map<string, CatalogItem[]>();
  if (!catalog || isForwardCurves) return groups;

  for (const item of catalog) {
    if (!showDiscontinued && item.Status === 'Archive') continue;
    const existing = groups.get(item.Code);
    if (existing) existing.push(item);
    else groups.set(item.Code, [item]);
  }

  for (const list of groups.values()) {
    list.sort(comparePeriods);
  }

  return groups;
}
